export type FacilityOperatingHours = {
  dayOfWeek: number;
  /** `HH:mm:ss` in form state. `null` on the wire when the time is not set. */
  openTime: string | null;
  closeTime: string | null;
  isClosed: boolean;
};

type OperatingHoursInput = {
  dayOfWeek?: number | null;
  openTime?: string | null;
  closeTime?: string | null;
  isClosed?: boolean | null;
  key?: unknown;
};

const CLOCK_TIME = /^(\d{1,2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?/;

/**
 * API operating times are TimeSpan values (`HH:mm` or `HH:mm:ss`).
 * TimePicker emits `YYYY-MM-DDTHH:mm`, which the day validator rejects.
 */
export const toOperatingTimeSpan = (
  value: string | null | undefined,
): string | null => {
  if (value == null) return null;
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  const clock = trimmed.includes('T')
    ? trimmed.slice(trimmed.indexOf('T') + 1)
    : trimmed;
  const match = CLOCK_TIME.exec(clock);
  if (match == null) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  const seconds = Number(match[3] || '0');
  if (hours > 23 || minutes > 59 || seconds > 59) return null;
  const pad = (part: number) => String(part).padStart(2, '0');
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
};

/** Value TimePicker can display and edit without writing a date back into the model. */
export const operatingTimeForPicker = (
  value: string | null | undefined,
): string => {
  const span = toOperatingTimeSpan(value);
  return span == null ? '' : `2000-01-01T${span}`;
};

/** Stores a TimePicker change as `HH:mm:ss`, or `''` when the input is cleared. */
export const operatingTimeFromPicker = (value: string): string =>
  toOperatingTimeSpan(value) ?? '';

const editorDay = (
  dayOfWeek: number,
  day: OperatingHoursInput | undefined,
): FacilityOperatingHours => {
  const openTime = toOperatingTimeSpan(day?.openTime) ?? '';
  const closeTime = toOperatingTimeSpan(day?.closeTime) ?? '';
  const noTimes = openTime.length === 0 && closeTime.length === 0;
  // Saved "no hours" and an explicit Closed day are both null times, so both load as open and blank.
  return {
    dayOfWeek,
    openTime,
    closeTime,
    isClosed: noTimes ? false : Boolean(day?.isClosed),
  };
};

/** Fills Monday–Sunday. Days with no times stay open with blank clocks. */
export const parseOperatingHours = (
  hours: readonly OperatingHoursInput[] | null | undefined,
): FacilityOperatingHours[] => {
  const byDay = new Map<number, OperatingHoursInput>();
  for (const day of hours ?? []) {
    const dayOfWeek = day.dayOfWeek;
    if (dayOfWeek == null || dayOfWeek < 1 || dayOfWeek > 7) continue;
    byDay.set(dayOfWeek, day);
  }
  return Array.from({ length: 7 }, (_, index) =>
    editorDay(index + 1, byDay.get(index + 1)),
  );
};

/**
 * Always one row per weekday. Times are `HH:mm:ss`.
 * A day with no times is closed and sends null times. The editor-only `key` is dropped.
 */
export const serializeOperatingHours = (
  hours: readonly OperatingHoursInput[] | null | undefined,
): FacilityOperatingHours[] => {
  const byDay = new Map<number, OperatingHoursInput>();
  for (const day of hours ?? []) {
    const dayOfWeek = day.dayOfWeek;
    if (dayOfWeek == null || dayOfWeek < 1 || dayOfWeek > 7) continue;
    byDay.set(dayOfWeek, day);
  }
  return Array.from({ length: 7 }, (_, index) => {
    const dayOfWeek = index + 1;
    const day = byDay.get(dayOfWeek);
    const openTime = toOperatingTimeSpan(day?.openTime);
    const closeTime = toOperatingTimeSpan(day?.closeTime);
    const isClosed =
      (openTime == null && closeTime == null) || Boolean(day?.isClosed);
    return {
      dayOfWeek,
      openTime: isClosed ? null : openTime,
      closeTime: isClosed ? null : closeTime,
      isClosed,
    };
  });
};

export const normalizeFacilityDetails = <T extends FacilityHoursValues>(
  details: T,
): T => {
  const next: T = {
    ...details,
    pickupHours: parseOperatingHours(details.pickupHours),
    receivingHours: parseOperatingHours(details.receivingHours),
    preferredAppointmentTime: preferredAppointmentTimeOrNull(
      details.preferredAppointmentTime,
    ),
    schedulingEmailCc: emptyToNull(details.schedulingEmailCc),
  };
  const stop = next as T & { customerAddressId?: string | null };
  if ('customerAddressId' in details && stop.customerAddressId == null) {
    stop.customerAddressId = null;
  }
  return next;
};

export const serializeFacilityDetails = <T extends FacilityHoursValues>(
  details: T,
): T => {
  const next: T = {
    ...details,
    pickupHours: serializeOperatingHours(details.pickupHours),
    receivingHours: serializeOperatingHours(details.receivingHours),
    preferredAppointmentTime: preferredAppointmentTimeOrNull(
      details.preferredAppointmentTime,
    ),
    schedulingEmailCc: emptyToNull(details.schedulingEmailCc),
  };
  if ('customerAddressId' in next) {
    delete (next as { customerAddressId?: string | null }).customerAddressId;
  }
  return next;
};

export const mapStopFacilityDetails = <
  TOrder extends {
    loads?: Array<{
      stops?: Array<{ facilityDetails?: FacilityHoursValues | null }> | null;
    }> | null;
  },
>(
  order: TOrder,
  mapDetails: <T extends FacilityHoursValues>(details: T) => T,
): TOrder => {
  if (!order.loads) return order;
  return {
    ...order,
    loads: order.loads.map((load) => {
      if (!load.stops) return load;
      return {
        ...load,
        stops: load.stops.map((stop) => {
          if (!stop.facilityDetails) return stop;
          return {
            ...stop,
            facilityDetails: mapDetails(stop.facilityDetails),
          };
        }),
      };
    }),
  };
};

export const MAX_PORTAL_CREDENTIALS_LENGTH = 500;

export const MAX_SCHEDULING_EMAIL_CC_LENGTH = 100;

export const FACILITY_DETAILS_VALIDATION_CODE = 'FacilityDetails.Validation';

export const PREFERRED_APPOINTMENT_TIMES = [
  'Morning',
  'Afternoon',
  'Evening',
] as const;

export type PreferredAppointmentTime =
  (typeof PREFERRED_APPOINTMENT_TIMES)[number];

const SINGLE_EMAIL = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/;

export const preferredAppointmentTimeOrNull = (
  value: string | null | undefined,
): PreferredAppointmentTime | null => {
  if (value === 'Morning' || value === 'Afternoon' || value === 'Evening') {
    return value;
  }
  return null;
};

const emptyToNull = (value: string | null | undefined): string | null => {
  if (value == null) return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
};

const minutesOfDay = (value: string | null | undefined): number | null => {
  if (value == null) return null;
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  const clock = trimmed.includes('T')
    ? trimmed.slice(trimmed.indexOf('T') + 1)
    : trimmed;
  const match = CLOCK_TIME.exec(clock);
  if (match == null) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  const seconds = Number(match[3] || '0');
  if (minutes > 59 || seconds > 59) return null;
  if (hours === 24) {
    if (minutes !== 0 || seconds !== 0) return null;
    return 24 * 60;
  }
  if (hours > 23) return null;
  return hours * 60 + minutes;
};

export const operatingHoursOrderError = (day: {
  isClosed?: boolean | null;
  openTime?: string | null;
  closeTime?: string | null;
}): string | null => {
  if (day.isClosed) return null;
  const open = minutesOfDay(day.openTime);
  const close = minutesOfDay(day.closeTime);
  if (open == null || close == null) return null;
  if (close > open) return null;
  return 'Close time must be after open time.';
};

const hourSchedules = (details: unknown): unknown[][] => {
  if (typeof details !== 'object' || details == null) return [];
  const record = details as {
    pickupHours?: unknown;
    receivingHours?: unknown;
  };
  const schedules: unknown[][] = [];
  if (Array.isArray(record.pickupHours)) schedules.push(record.pickupHours);
  if (Array.isArray(record.receivingHours)) {
    schedules.push(record.receivingHours);
  }
  return schedules;
};

export const facilityScheduleOrderError = (details: unknown): string | null => {
  for (const schedule of hourSchedules(details)) {
    for (const day of schedule) {
      if (typeof day !== 'object' || day == null) continue;
      const message = operatingHoursOrderError(
        day as {
          isClosed?: boolean | null;
          openTime?: string | null;
          closeTime?: string | null;
        },
      );
      if (message != null) return message;
    }
  }
  return null;
};

export const schedulingEmailCcError = (
  value: string | null | undefined,
  showIncomplete = false,
): string | null => {
  if (value == null || value.trim().length === 0) return null;
  if (value.length > MAX_SCHEDULING_EMAIL_CC_LENGTH) {
    return 'CC scheduling email must be 100 characters or fewer.';
  }
  if (value.includes(',') || value.includes(';') || /\s/.test(value)) {
    return 'Enter a single email address.';
  }
  if (!showIncomplete) return null;
  if (!SINGLE_EMAIL.test(value.trim())) {
    return 'Enter a single email address.';
  }
  return null;
};

const messageFromValidationItem = (item: unknown): string | null => {
  if (typeof item !== 'object' || item == null) return null;
  const record = item as {
    code?: string;
    message?: string;
    errorMessage?: string;
  };
  if (record.code !== FACILITY_DETAILS_VALIDATION_CODE) return null;
  return record.message ?? record.errorMessage ?? null;
};

export const facilityDetailsValidationMessage = (
  error: unknown,
): string | null => {
  if (Array.isArray(error)) {
    const messages = error
      .map((item) => messageFromValidationItem(item))
      .filter((message): message is string => message != null);
    return messages.length === 0 ? null : messages.join('\n');
  }
  if (error instanceof Error) {
    return facilityDetailsValidationMessage(error.cause);
  }
  return null;
};

/** Portal URL and login info only exist for this scheduling method. */
export const PORTAL_SCHEDULING_METHOD = 'Portal';

/** Computed by the API, never sent back. */
export type FacilityLinkStatus =
  | 'Manual'
  | 'Linked'
  | 'Overridden'
  | 'Detached';

type FacilityValues = {
  appointmentType: string;
  schedulingMethod: string;
  schedulingMethodDetails: string;
  schedulingEmailCc: string | null;
  portalUrl: string;
  /** Decrypted JSON credential record. Stored encrypted server-side. */
  portalCredentials: string;
  pickupHoursNotes: string;
  receivingHoursNotes: string;
  receivingUsesPickupHours: boolean;
  preferredAppointmentTime: PreferredAppointmentTime | null;
  pickupHours: FacilityOperatingHours[];
  receivingHours: FacilityOperatingHours[];
};

/** The facility record on an address book entry. Many stops may link to it. */
export type FacilityDetails = FacilityValues & {
  id: number;
  version: number;
};

/** A stop's own values. Always explicit; never resolved from the facility at read time. */
export type StopFacilityDetails = FacilityValues & {
  facilityDetailsId: number | null;
  facilityDetailsVersion: number | null;
  customerAddressId: string | null;
  isOverridden: boolean;
  linkStatus: FacilityLinkStatus;
  /** The linked facility has been saved since these values were copied. */
  facilityHasChanges: boolean;
};

/** The slice of facility values the weekly hours editor owns. */
export type FacilityHoursValues = Pick<
  FacilityValues,
  | 'pickupHours'
  | 'receivingHours'
  | 'pickupHoursNotes'
  | 'receivingHoursNotes'
  | 'receivingUsesPickupHours'
  | 'preferredAppointmentTime'
  | 'schedulingEmailCc'
>;

const createWeek = (): FacilityOperatingHours[] =>
  Array.from({ length: 7 }, (_, index) => ({
    dayOfWeek: index + 1,
    openTime: '',
    closeTime: '',
    isClosed: false,
  }));

const facilityValueFields = (source: FacilityValues): FacilityValues => ({
  appointmentType: source.appointmentType,
  schedulingMethod: source.schedulingMethod,
  schedulingMethodDetails: source.schedulingMethodDetails,
  schedulingEmailCc: emptyToNull(source.schedulingEmailCc),
  portalUrl: source.portalUrl,
  portalCredentials: source.portalCredentials,
  pickupHoursNotes: source.pickupHoursNotes,
  receivingHoursNotes: source.receivingHoursNotes,
  receivingUsesPickupHours: Boolean(source.receivingUsesPickupHours),
  preferredAppointmentTime: preferredAppointmentTimeOrNull(
    source.preferredAppointmentTime,
  ),
  pickupHours: parseOperatingHours(source.pickupHours),
  receivingHours: parseOperatingHours(source.receivingHours),
});

const createValues = (): FacilityValues => ({
  appointmentType: '',
  schedulingMethod: '',
  schedulingMethodDetails: '',
  schedulingEmailCc: null,
  portalUrl: '',
  portalCredentials: '',
  pickupHoursNotes: '',
  receivingHoursNotes: '',
  receivingUsesPickupHours: false,
  preferredAppointmentTime: null,
  pickupHours: createWeek(),
  receivingHours: createWeek(),
});

export const createFacilityDetails = (): FacilityDetails => ({
  ...createValues(),
  id: 0,
  version: 0,
});

export const createStopFacilityDetails = (): StopFacilityDetails => ({
  ...createValues(),
  facilityDetailsId: null,
  facilityDetailsVersion: null,
  customerAddressId: null,
  isOverridden: false,
  linkStatus: 'Manual',
  facilityHasChanges: false,
});

/** Copies a facility's current values onto a stop and links the two. */
export const copyFacilityToStop = (
  facility: FacilityDetails,
  customerAddressId: string | null = null,
): StopFacilityDetails => ({
  ...facilityValueFields(facility),
  facilityDetailsId: facility.id,
  facilityDetailsVersion: facility.version,
  customerAddressId,
  isOverridden: false,
  linkStatus: 'Linked',
  facilityHasChanges: false,
});

/** Promotes a stop's values into a new address book facility record. */
export const stopFacilityToFacility = (
  stop: StopFacilityDetails,
): FacilityDetails => ({
  ...facilityValueFields(stop),
  id: 0,
  version: 0,
});

export const linkStopToSavedAddress = (
  current: StopFacilityDetails,
  saved: { id?: string | null; facilityDetails?: FacilityDetails | null },
): StopFacilityDetails => {
  const facility = saved.facilityDetails;
  if (facility == null) {
    return {
      ...current,
      customerAddressId: saved.id ?? current.customerAddressId,
      isOverridden: false,
      linkStatus: 'Linked',
      facilityHasChanges: false,
    };
  }
  return {
    ...copyFacilityToStop(
      normalizeFacilityDetails(facility),
      saved.id ?? current.customerAddressId,
    ),
    facilityDetailsVersion: facility.version,
  };
};

/** Deep-copies hours so a copied stop does not share arrays with the source. */
export const cloneStopFacilityDetails = (
  facilityDetails?: StopFacilityDetails | null,
): StopFacilityDetails | undefined => {
  if (facilityDetails == null) return undefined;
  return {
    ...facilityDetails,
    pickupHours: parseOperatingHours(facilityDetails.pickupHours),
    receivingHours: parseOperatingHours(facilityDetails.receivingHours),
  };
};

export const facilityLinkLabel = (details: StopFacilityDetails): string => {
  switch (details.linkStatus) {
    case 'Linked':
      return details.facilityHasChanges
        ? 'Linked to facility - facility has been updated'
        : 'Linked to facility';
    case 'Overridden':
      return 'Overridden for this stop';
    case 'Detached':
      return 'Facility no longer exists - using stop values';
    default:
      return 'Entered for this stop';
  }
};
